export interface Transaction {
  id: string;
  amount: number;
  status: string;
  type: string;
  created_at: string;
  platform_id: string;
  department_id?: string;
  entity_id?: string;
  customer_name?: string;
  customer_email?: string;
  metadata?: any;
  platforms?: { account_name: string }[];
  platform_transaction_id?: string;
  description?: string;
  currency?: string;
  is_duplicate?: boolean;
  categorization_method?: string;
}

export interface LineItem {
  id: string;
  transaction_id: string;
  product_id?: string;
  name: string;
  type: string;
  sku?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount: number;
  platform_item_id?: string;
  metadata?: any;
  created_at?: string;
}

export interface TransactionDetail extends Transaction {
  line_items: LineItem[];
  platform_detail?: {
    account_name: string;
    account_id: string;
    platform_type: string;
  };
  department_detail?: {
    id: string;
    name: string;
    slug: string;
  };
  categorization_rule?: {
    id: string;
    priority: number;
    rule_type: string;
    match_field: string;
    match_operator: string;
    match_value: string;
    description?: string;
  };
  categorization_rule_id?: string;
  duplicate_of?: string;
  duplicate_reason?: string;
  line_items_synced?: boolean;
  enrichment_source?: string;
}

export interface DashboardStats {
  totalTransactions: number;
  grossRevenue: number;
  totalRefunds: number;
  netRevenue: number;
  monthlyRevenue: number;
  failedPayments: number;
  refunds: number;
  chargebacks: number;
  platformStats: PlatformStat[];
  recentTransactions: Transaction[];
  period?: {
    startDate: string;
    endDate: string;
    days: number;
  };
  successfulPayments?: number;
  successRate?: number;
  averageTransactionValue?: number;
  refundRate?: number;
}

export interface PlatformStat {
  name: string;
  transactions: number;
  revenue: number;
  successRate: number;
  refunds?: number;
  netRevenue?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface Platform {
  id: string;
  account_name: string;
  account_id?: string;
  name?: string;
}

export interface Department {
  id: string;
  name: string;
  entity_id: string;
  is_active: boolean;
  entity?: Entity;
}

export interface Entity {
  id: string;
  name: string;
  is_active: boolean;
}

export interface DepartmentRule {
  id: string;
  department_id: string;
  priority: number;
  rule_type: string;
  match_field: string;
  match_operator: string;
  match_value: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  department?: { id: string; name: string; slug: string };
}

export interface DepartmentRevenue {
  department_name: string;
  department_id?: string;
  entity_name?: string;
  successful_payments: number;
  refund_count: number;
  total_revenue: number;
  total_refunds: number;
  net_revenue: number;
}

export interface KpiMetric {
  current: number;
  comparison: number;
  delta: number;
  deltaPct: number | null;
}

export interface DepartmentPeriodData {
  departmentId: string;
  name: string;
  txCount: number;
  grossRevenue: number;
  totalRefunds: number;
  netRevenue: number;
  comparisonNet: number;
  deltaPct: number | null;
}

export interface DashboardAlert {
  type: string;
  severity: 'red' | 'orange' | 'yellow';
  message: string;
  department?: string;
}

export interface PlatformHealthData {
  id: string;
  name: string;
  type: string;
  lastSync: string | null;
  hoursSinceSync: number | null;
}

export interface DailySeries {
  day: string;
  netRevenue: number;
  txCount: number;
}

export interface ExecutiveDashboardData {
  period: {
    start: string;
    end: string;
    comparisonStart: string;
    comparisonEnd: string;
    label: string;
  };
  kpis: {
    netRevenue: KpiMetric;
    txCount: KpiMetric;
    avgOrder: KpiMetric;
    refundRate: KpiMetric;
    grossRevenue: KpiMetric;
    totalRefunds: KpiMetric;
  };
  dailySeries: DailySeries[];
  comparisonDailySeries: DailySeries[];
  departments: DepartmentPeriodData[];
  alerts: DashboardAlert[];
  platformHealth: PlatformHealthData[];
  recentTransactions: Transaction[];
}

export interface MembershipMonth {
  month: string;
  recurring: number;
  recurringPayers: number;
  l4: number;
  l5: number;
}

export interface PostSchoolEvent {
  schoolEvent: string;
  ended: string;
  attendees: number;
  ticketRev: number;
  post7d: number;
  post30d: number;
  post60d: number;
  clubs60d: number;
  clubBuyers60d: number;
}

export interface GrowthEnginesData {
  membership: {
    months: MembershipMonth[];
    subs: { active: number; inTrial: number; cancelled: number };
    latestMonth: string | null;
    latestRecurring: number;
    momPct: number | null;
  };
  postSchool: {
    events: PostSchoolEvent[];
    totals: {
      events: number;
      attendees: number;
      ticketRev: number;
      post60: number;
      clubs60: number;
      clubBuyers60: number;
    };
    attachMultiple: number | null;
    clubAttachRate: number | null;
  };
}

// Customer types
export interface Customer {
  id: string;
  normalized_email?: string;
  display_email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  source_platform?: string;
  source_channel?: string;
  contact_created_date?: string;
  first_purchase_date?: string;
  last_purchase_date?: string;
  total_transactions: number;
  total_revenue: number;
  total_refunds: number;
  ltv: number;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerDetail extends Customer {
  transactions: (Transaction & { platform?: Platform })[];
  callaway_invoices: {
    id: string;
    invoice_number: string;
    amount: number;
    invoice_date: string;
    invoice_type: string;
    status: string;
    customer_name?: string;
    reconciled: boolean;
  }[];
  aliases: {
    id: string;
    alias_type: string;
    alias_value: string;
    source_platform?: string;
    match_confidence?: number;
    created_at: string;
  }[];
}

export interface CustomerStats {
  totalCustomers: number;
  avgLtv: number;
  newThisMonth: number;
  unlinkedCallawayCount: number;
  topSourcePlatforms: { platform: string; count: number }[];
}

export interface UnlinkedCallawayInvoice {
  id: string;
  invoice_number: string;
  customer_name: string;
  amount: number;
  invoice_date: string;
  invoice_type: string;
  candidates: {
    id: string;
    full_name?: string;
    display_email?: string;
    postal_code?: string;
    ltv: number;
    confidence: number;
  }[];
}

// Keap Order types
export interface KeapOrder {
  id: string;
  keap_order_id: number;
  keap_contact_id?: number;
  order_type: 'club' | 'trade_in';
  title?: string;
  status?: string;
  order_date?: string;
  total: number;
  total_paid: number;
  refund_total: number;
  contact_email?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_full_name?: string;
  contact_company?: string;
  shipping_phone?: string;
  shipping_street1?: string;
  shipping_street2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_zip?: string;
  shipping_country?: string;
  merchant_account_name?: string;
  customer_id?: string;
  linked_transaction_id?: string;
  linked_callaway_invoice_id?: string;
  transaction_match_method?: string;
  transaction_match_confidence?: number;
  callaway_match_method?: string;
  callaway_match_confidence?: number;
  notes?: string;
  created_at: string;
}

export interface KeapOrderDetail extends KeapOrder {
  items: {
    id: string;
    name?: string;
    description?: string;
    type?: string;
    quantity: number;
    price: number;
    product_id?: number;
    product_name?: string;
    product_sku?: string;
  }[];
  linked_transaction?: Transaction & { platform?: Platform };
  linked_callaway?: {
    id: string;
    invoice_number: string;
    amount: number;
    invoice_date: string;
    invoice_type: string;
    status: string;
    customer_name?: string;
  };
}

export interface KeapOrderStats {
  totalClubOrders: number;
  totalTradeInOrders: number;
  unmatchedToTransaction: number;
  unmatchedToCallaway: number;
  totalClubRevenue: number;
  totalTradeInRevenue: number;
}

export class ApiService {
  private API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';
  private platformCache: Platform[] = [];
  private platformMap: Map<string, Platform> = new Map();
  private departmentCache: Department[] = [];
  private departmentMap: Map<string, Department> = new Map();

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Platform methods
  public async loadPlatforms(): Promise<void> {
    try {
      const response = await this.request<{ data: Platform[] }>('/platforms');
      this.platformCache = response.data;
      this.platformMap.clear();
      this.platformCache.forEach(platform => {
        this.platformMap.set(platform.id, platform);
      });
    } catch (error) {
      console.error('Error loading platforms:', error);
    }
  }

  public getPlatformName(platformId: string): string {
    const platform = this.platformMap.get(platformId);
    return platform?.account_name || 'Unknown Platform';
  }

  public getPlatforms(): Platform[] {
    return this.platformCache;
  }

  public async syncPlatform(platformId: string): Promise<void> {
    await this.request(`/platforms/${platformId}/sync`, { method: 'POST' });
  }

  // Department methods
  public async loadDepartments(): Promise<void> {
    try {
      const response = await this.request<{ success: boolean; data: Department[] }>('/departments');
      this.departmentCache = response.data || [];
      this.departmentMap.clear();
      this.departmentCache.forEach(dept => {
        this.departmentMap.set(dept.id, dept);
      });
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  }

  public getDepartments(): Department[] {
    return this.departmentCache;
  }

  public getDepartmentName(departmentId: string): string {
    const dept = this.departmentMap.get(departmentId);
    return dept?.name || 'Uncategorized';
  }

  public async getDepartmentRevenue(startDate?: string, endDate?: string): Promise<DepartmentRevenue[]> {
    try {
      let url = '/departments/revenue';
      if (startDate && endDate) {
        url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
      }
      const response = await this.request<{ success: boolean; data: DepartmentRevenue[] }>(url);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching department revenue:', error);
      return [];
    }
  }

  public async getEntities(): Promise<Entity[]> {
    try {
      const response = await this.request<{ success: boolean; data: Entity[] }>('/departments/entities');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching entities:', error);
      return [];
    }
  }

  // Dashboard & Analytics methods
  public async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await this.request<{ success: boolean; data: DashboardStats }>('/analytics/dashboard-stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        totalTransactions: 0, grossRevenue: 0, totalRefunds: 0, netRevenue: 0,
        monthlyRevenue: 0, failedPayments: 0, refunds: 0, chargebacks: 0,
        platformStats: [], recentTransactions: []
      };
    }
  }

  public async getDateFilteredDashboardStats(startDate: string, endDate: string): Promise<DashboardStats> {
    try {
      const response = await this.request<{ success: boolean; data: DashboardStats }>(
        `/analytics/date-filtered-stats?startDate=${startDate}&endDate=${endDate}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching date-filtered stats:', error);
      return this.getDashboardStats();
    }
  }

  public async getRevenueAnalytics() {
    try {
      const response = await this.request<{ success: boolean; data: any }>('/analytics/revenue');
      return response.data;
    } catch (error) {
      console.error('Error fetching revenue analytics:', error);
      return null;
    }
  }

  public async getRefundAnalytics() {
    try {
      const response = await this.request<{ success: boolean; data: any }>('/analytics/refunds');
      return response.data;
    } catch (error) {
      console.error('Error fetching refund analytics:', error);
      return null;
    }
  }

  // Transaction methods
  public async getTransactions(
    page: number = 1,
    pageSize: number = 20,
    platformFilter?: string,
    statusFilter?: string,
    searchFilter?: string,
    startDateFilter?: string,
    endDateFilter?: string,
    departmentFilter?: string,
    emailFilter?: string
  ): Promise<PaginatedResponse<Transaction>> {
    try {
      let url = `/payments?page=${page}&pageSize=${pageSize}`;
      if (platformFilter) url += `&platform=${platformFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchFilter) url += `&search=${encodeURIComponent(searchFilter)}`;
      if (startDateFilter) url += `&startDate=${encodeURIComponent(startDateFilter)}`;
      if (endDateFilter) url += `&endDate=${encodeURIComponent(endDateFilter)}`;
      if (departmentFilter) url += `&department=${departmentFilter}`;
      if (emailFilter) url += `&emailFilter=${emailFilter}`;

      const response = await this.request<{ success: boolean; data: Transaction[]; pagination: any }>(url);

      if (response.success && response.data) {
        return {
          data: response.data || [],
          page: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        };
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return { data: [], total: 0, page: 1, pageSize: 20, totalPages: 0 };
    }
  }

  // Transaction Detail
  public async getTransactionDetail(id: string): Promise<TransactionDetail | null> {
    try {
      const response = await this.request<{ success: boolean; data: TransactionDetail }>(`/payments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction detail:', error);
      return null;
    }
  }

  // Categorization Rules
  public async getDepartmentRules(): Promise<DepartmentRule[]> {
    try {
      const response = await this.request<{ success: boolean; data: DepartmentRule[] }>('/departments/rules');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching department rules:', error);
      return [];
    }
  }

  public async createDepartmentRule(rule: Partial<DepartmentRule>): Promise<DepartmentRule | null> {
    try {
      const response = await this.request<{ success: boolean; data: DepartmentRule }>('/departments/rules', {
        method: 'POST',
        body: JSON.stringify(rule),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating rule:', error);
      return null;
    }
  }

  public async updateDepartmentRule(id: string, updates: Partial<DepartmentRule>): Promise<DepartmentRule | null> {
    try {
      const response = await this.request<{ success: boolean; data: DepartmentRule }>(`/departments/rules/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      return response.data;
    } catch (error) {
      console.error('Error updating rule:', error);
      return null;
    }
  }

  public async deleteDepartmentRule(id: string): Promise<boolean> {
    try {
      await this.request(`/departments/rules/${id}`, { method: 'DELETE' });
      return true;
    } catch (error) {
      console.error('Error deleting rule:', error);
      return false;
    }
  }

  public async getDepartmentStats(): Promise<any> {
    try {
      const response = await this.request<{ success: boolean; data: any }>('/departments/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching department stats:', error);
      return null;
    }
  }

  public async runCategorization(): Promise<any> {
    try {
      const response = await this.request<{ success: boolean; data: any }>('/departments/categorize', {
        method: 'POST',
      });
      return response.data;
    } catch (error) {
      console.error('Error running categorization:', error);
      return null;
    }
  }

  public async manualAssignTransaction(transactionId: string, departmentSlug: string): Promise<boolean> {
    try {
      await this.request(`/departments/transactions/${transactionId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ department_slug: departmentSlug }),
      });
      return true;
    } catch (error) {
      console.error('Error assigning transaction:', error);
      return false;
    }
  }

  public async getExecutiveDashboard(period: string = 'mtd', startDate?: string, endDate?: string, excludeDepts?: string[]): Promise<ExecutiveDashboardData | null> {
    try {
      const params = new URLSearchParams({ period });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (excludeDepts && excludeDepts.length > 0) params.set('exclude', excludeDepts.join(','));
      const response = await this.request<{ success: boolean; data: ExecutiveDashboardData }>(`/analytics/executive-dashboard?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching executive dashboard:', error);
      return null;
    }
  }

  public async getGrowthEngines(): Promise<GrowthEnginesData | null> {
    try {
      const response = await this.request<{ success: boolean; data: GrowthEnginesData }>('/analytics/growth-engines');
      return response.data;
    } catch (error) {
      console.error('Error fetching growth engines:', error);
      return null;
    }
  }

  // Customer methods
  public async getCustomers(params: {
    page?: number; limit?: number; search?: string; sortBy?: string; sortDir?: string; source?: string;
  } = {}): Promise<{ data: Customer[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const query = new URLSearchParams();
      if (params.page) query.set('page', String(params.page));
      if (params.limit) query.set('limit', String(params.limit));
      if (params.search) query.set('search', params.search);
      if (params.sortBy) query.set('sortBy', params.sortBy);
      if (params.sortDir) query.set('sortDir', params.sortDir);
      if (params.source) query.set('source', params.source);
      const response = await this.request<{ success: boolean; data: Customer[]; pagination: any }>(`/customers?${query}`);
      return { data: response.data || [], pagination: response.pagination };
    } catch (error) {
      console.error('Error fetching customers:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  public async getCustomer(id: string): Promise<CustomerDetail | null> {
    try {
      const response = await this.request<{ success: boolean; data: CustomerDetail }>(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  }

  public async getCustomerStats(): Promise<CustomerStats> {
    try {
      const response = await this.request<{ success: boolean; data: CustomerStats }>('/customers/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching customer stats:', error);
      return { totalCustomers: 0, avgLtv: 0, newThisMonth: 0, unlinkedCallawayCount: 0, topSourcePlatforms: [] };
    }
  }

  public async getUnlinkedCallawayInvoices(): Promise<UnlinkedCallawayInvoice[]> {
    try {
      const response = await this.request<{ success: boolean; data: UnlinkedCallawayInvoice[] }>('/customers/unlinked');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching unlinked invoices:', error);
      return [];
    }
  }

  public async createCustomer(data: {
    email?: string; firstName?: string; lastName?: string; phone?: string;
    address?: { line1?: string; line2?: string; city?: string; state?: string; postalCode?: string; country?: string };
    notes?: string;
  }): Promise<Customer | null> {
    try {
      const response = await this.request<{ success: boolean; data: Customer }>('/customers', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      return null;
    }
  }

  public async updateCustomer(id: string, data: any): Promise<Customer | null> {
    try {
      const response = await this.request<{ success: boolean; data: Customer }>(`/customers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response.data;
    } catch (error) {
      console.error('Error updating customer:', error);
      return null;
    }
  }

  public async mergeCustomers(primaryId: string, secondaryId: string): Promise<boolean> {
    try {
      await this.request(`/customers/${primaryId}/merge`, {
        method: 'POST',
        body: JSON.stringify({ secondaryId }),
      });
      return true;
    } catch (error) {
      console.error('Error merging customers:', error);
      return false;
    }
  }

  public async linkCallawayInvoice(customerId: string, invoiceId: string): Promise<boolean> {
    try {
      await this.request(`/customers/${customerId}/link-invoice`, {
        method: 'POST',
        body: JSON.stringify({ invoiceId }),
      });
      return true;
    } catch (error) {
      console.error('Error linking invoice:', error);
      return false;
    }
  }

  public async unlinkCallawayInvoice(customerId: string, invoiceId: string): Promise<boolean> {
    try {
      await this.request(`/customers/${customerId}/link-invoice/${invoiceId}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Error unlinking invoice:', error);
      return false;
    }
  }

  // Keap Order methods
  public async getKeapOrders(params: {
    page?: number; limit?: number; search?: string; type?: string; matchStatus?: string;
    startDate?: string; endDate?: string; sortBy?: string; sortDir?: string;
  } = {}): Promise<{ data: KeapOrder[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const query = new URLSearchParams();
      if (params.page) query.set('page', String(params.page));
      if (params.limit) query.set('limit', String(params.limit));
      if (params.search) query.set('search', params.search);
      if (params.type) query.set('type', params.type);
      if (params.matchStatus) query.set('matchStatus', params.matchStatus);
      if (params.startDate) query.set('startDate', params.startDate);
      if (params.endDate) query.set('endDate', params.endDate);
      if (params.sortBy) query.set('sortBy', params.sortBy);
      if (params.sortDir) query.set('sortDir', params.sortDir);
      const response = await this.request<{ success: boolean; data: KeapOrder[]; pagination: any }>(`/keap-orders?${query}`);
      return { data: response.data || [], pagination: response.pagination };
    } catch (error) {
      console.error('Error fetching keap orders:', error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  public async getKeapOrder(id: string): Promise<KeapOrderDetail | null> {
    try {
      const response = await this.request<{ success: boolean; data: KeapOrderDetail }>(`/keap-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching keap order:', error);
      return null;
    }
  }

  public async getKeapOrderStats(): Promise<KeapOrderStats> {
    try {
      const response = await this.request<{ success: boolean; data: KeapOrderStats }>('/keap-orders/stats');
      return response.data;
    } catch (error) {
      return { totalClubOrders: 0, totalTradeInOrders: 0, unmatchedToTransaction: 0, unmatchedToCallaway: 0, totalClubRevenue: 0, totalTradeInRevenue: 0 };
    }
  }

  public async syncKeapOrders(days = 7): Promise<any> {
    try {
      const response = await this.request<{ success: boolean; data: any }>(`/keap-orders/sync?days=${days}`, { method: 'POST' });
      return response.data;
    } catch (error) {
      console.error('Error syncing keap orders:', error);
      return null;
    }
  }

  public async runKeapOrderMatching(): Promise<any> {
    try {
      const response = await this.request<{ success: boolean; data: any }>('/keap-orders/match', { method: 'POST' });
      return response.data;
    } catch (error) {
      console.error('Error matching keap orders:', error);
      return null;
    }
  }

  public async linkKeapOrderTransaction(orderId: string, transactionId: string): Promise<boolean> {
    try {
      await this.request(`/keap-orders/${orderId}/link-transaction`, {
        method: 'POST', body: JSON.stringify({ transactionId }),
      });
      return true;
    } catch { return false; }
  }

  public async unlinkKeapOrderTransaction(orderId: string): Promise<boolean> {
    try {
      await this.request(`/keap-orders/${orderId}/link-transaction`, { method: 'DELETE' });
      return true;
    } catch { return false; }
  }

  public async linkKeapOrderCallaway(orderId: string, callawayInvoiceId: string): Promise<boolean> {
    try {
      await this.request(`/keap-orders/${orderId}/link-callaway`, {
        method: 'POST', body: JSON.stringify({ callawayInvoiceId }),
      });
      return true;
    } catch { return false; }
  }

  public async unlinkKeapOrderCallaway(orderId: string): Promise<boolean> {
    try {
      await this.request(`/keap-orders/${orderId}/link-callaway`, { method: 'DELETE' });
      return true;
    } catch { return false; }
  }

  public async runCustomerResolution(): Promise<any> {
    try {
      const response = await this.request<{ success: boolean; data: any }>('/customers/resolve', { method: 'POST' });
      return response.data;
    } catch (error) {
      console.error('Error running customer resolution:', error);
      return null;
    }
  }

  public async getRecentTransactions(): Promise<Transaction[]> {
    try {
      await this.loadPlatforms();
      const response = await this.request<{ success: boolean; data: DashboardStats }>('/analytics/dashboard-stats');
      return response.data.recentTransactions || [];
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      return [];
    }
  }
}

export const apiService = new ApiService();
